/**
 * PostHog Analytics Wrapper — SYRA
 * All events use the `syra.` prefix.
 * PII is never sent — emails are SHA-256 hashed.
 */
import posthog from "posthog-js";

const POSTHOG_READY = !!import.meta.env.VITE_POSTHOG_KEY;

// ── Helpers ─────────────────────────────────────────────────────────

async function hashEmail(email: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(email.toLowerCase().trim());
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// ── Core API ────────────────────────────────────────────────────────

export function capture(event: string, properties?: Record<string, unknown>) {
  if (!POSTHOG_READY) return;
  posthog.capture(`syra.${event}`, properties);
}

export async function identify(userId: string, email?: string, traits?: Record<string, unknown>) {
  if (!POSTHOG_READY) return;
  const hashedEmail = email ? await hashEmail(email) : undefined;
  posthog.identify(userId, {
    ...(hashedEmail ? { email_hash: hashedEmail } : {}),
    ...traits,
  });
}

export function group(orgId: string, orgTraits?: Record<string, unknown>) {
  if (!POSTHOG_READY) return;
  posthog.group("org", orgId, orgTraits);
}

export function reset() {
  if (!POSTHOG_READY) return;
  posthog.reset();
}

// ── Standardized Events ─────────────────────────────────────────────

export const events = {
  signupStarted: () => capture("signup_started"),
  signupCompleted: () => capture("signup_completed"),
  onboardingStep: (step: string) => capture("onboarding_step", { step }),
  whatsappConnected: () => capture("whatsapp_connected"),
  firstMessageSent: () => capture("first_message_sent"),
  agentCreated: (agentId?: string) => capture("agent_created", { agent_id: agentId }),
  skillExecuted: (skillName: string) => capture("skill_executed", { skill_name: skillName }),
  ticketOpened: (ticketId?: string) => capture("ticket_opened", { ticket_id: ticketId }),
  ticketResolved: (ticketId?: string) => capture("ticket_resolved", { ticket_id: ticketId }),
  billingUpgraded: (plan: string) => capture("billing_upgraded", { plan }),
  billingDowngraded: (plan: string) => capture("billing_downgraded", { plan }),
} as const;
